import {
  GetInteractionRoomRoomIdEmbedCodeOptions,
  GetInteractionRoomRoomIdEmbedCodeResult,
  getInteractionRoomRoomIdEmbedCode,
  GetInteractionRoomRoomIdEmbedCodeFields,
} from '../fetchers/interaction_room/room_id/embed_code';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetInteractionRoomRoomIdEmbedCodeOptions<
  Fields extends GetInteractionRoomRoomIdEmbedCodeFields,
> = GetInteractionRoomRoomIdEmbedCodeOptions<Fields>;

/**
 * Get the HTML embed code for an interaction room
 * This method returns the HTML embed code and related data for the specified interaction room.
 */

export function useGetInteractionRoomRoomIdEmbedCode<
  Fields extends GetInteractionRoomRoomIdEmbedCodeFields,
>(
  getOptions:
    | UseGetInteractionRoomRoomIdEmbedCodeOptions<Fields>
    | (() => UseGetInteractionRoomRoomIdEmbedCodeOptions<Fields> | null),
  config?: SWRConfiguration<GetInteractionRoomRoomIdEmbedCodeResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/interaction_rooms/${options.where.roomType}/${
          options.where.roomId
        }/embed_code${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getInteractionRoomRoomIdEmbedCode({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetInteractionRoomRoomIdEmbedCode, {
    endpoint: '/interaction_rooms/:roomType/:roomId/embed_code',
    method: 'GET',
  });
}

/**
 * Get the HTML embed code for an interaction room
 * This method returns the HTML embed code and related data for the specified interaction room.
 */

export function useGetInteractionRoomRoomIdEmbedCodeLazy<
  Fields extends GetInteractionRoomRoomIdEmbedCodeFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetInteractionRoomRoomIdEmbedCodeResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetInteractionRoomRoomIdEmbedCodeOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/interaction_rooms/${options.where.roomType}/${
            options.where.roomId
          }/embed_code${serializeQuery(options)}`,
          getInteractionRoomRoomIdEmbedCode({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetInteractionRoomRoomIdEmbedCodeLazy, {
    endpoint: '/interaction_rooms/:roomType/:roomId/embed_code',
    method: 'GET',
  });
}
